import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SolicitudSaludIrrecuperableRequest, EvaluacionMedicaExistente, ComisionExistente, EvaluacionMedicoContralor, ApelacionListItem, ExpedienteDetalle, InstitucionDetalle, TrazabilidadBitacora, ExpedienteCompletoResponse } from '@models/evaluacion-medica.interface';
import { environment } from '@env/environment';
import { firstValueFrom } from 'rxjs';
import { BackendAuthService } from './backend-auth.service';
import { SessionService } from './session.service';

@Injectable({
  providedIn: 'root'
})
export class SolicitudService {

  constructor(private http: HttpClient, private authService: BackendAuthService, private sessionService: SessionService) { }

  async crearSolicitud(solicitudRequest: SolicitudSaludIrrecuperableRequest): Promise<any>{
    const tokenRes = await this.authService.obtenerToken();
    const token = tokenRes.access_token;
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'session_id': this.sessionService.getToken() || '' });
    const url = environment.backendUrl + '/esi-expedientes';
    return firstValueFrom(this.http.post(url, solicitudRequest, { headers }));
  }

  async listarSolicitudesFuncionario(rutFuncionario: string): Promise<any>{
    const tokenRes = await this.authService.obtenerToken();
    const token = tokenRes.access_token;
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'session_id': this.sessionService.getToken() || '' });
    const url = environment.backendUrl + '/esi-expedientes-paginated';
    const body = { exp_rut_fun: rutFuncionario };
    return firstValueFrom(this.http.post(url, body, { headers }));
  }

  async listarSolicitudesFiltradas(filtros: any): Promise<any>{
    const tokenRes = await this.authService.obtenerToken();
    const token = tokenRes.access_token;
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'session_id': this.sessionService.getToken() || '' });
    const url = environment.backendUrl + '/esi-expedientes-paginated';
    return firstValueFrom(this.http.post(url, filtros, { headers }));
  }

  async obtenerDetalleEvalucionMedica(expId: number): Promise<{ evaluacion_existente: EvaluacionMedicaExistente | null, documentos: any[], expediente: ExpedienteDetalle | null, institucion: InstitucionDetalle | null }> {
    const tokenRes = await this.authService.obtenerToken();
    const token = tokenRes.access_token;
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'session_id': this.sessionService.getToken() || '' });
    const url = environment.backendUrl + '/esi-expediente-evaluacion-medica' + `?exp_id=${expId}`;
    const body = {};
    return firstValueFrom(this.http.post<any>(url, body, { headers })).then(res => ({
      evaluacion_existente: res.data?.evaluacion_existente || null,
      documentos: res.data?.documentos || [],
      expediente: res.data?.expediente || null,
      institucion: res.data?.institucion || null,
    }));
  }

  async obtenerDetalleComisionMedica(expId: number): Promise<{ comision_existente: ComisionExistente | null, evaluacion_medico_contralor: EvaluacionMedicoContralor | null }> {
    const tokenRes = await this.authService.obtenerToken();
    const token = tokenRes.access_token;
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'session_id': this.sessionService.getToken() || '' });
    const url = environment.backendUrl + '/esi-expediente-comision-medica' + `?exp_id=${expId}`;
    const body = {};
    return firstValueFrom(this.http.post<any>(url, body, { headers })).then(res => ({
      comision_existente: res.data?.comision_existente || null,
      evaluacion_medico_contralor: res.data?.evaluacion_medico_contralor || null
    }));
  }

  async obtenerApelaciones(expId: number): Promise<ApelacionListItem[]> {
    const tokenRes = await this.authService.obtenerToken();
    const token = tokenRes.access_token;
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'session_id': this.sessionService.getToken() || '' });
    const url = environment.backendUrl + '/esi-expediente-apelacion' + `?exp_id=${expId}`;
    const body = {};
    return firstValueFrom(this.http.post<any>(url, body, { headers })).then(res => res.data as ApelacionListItem[]);
  }

  async obtenerTrazabilidad(expId: number): Promise<TrazabilidadBitacora[]> {
    const tokenRes = await this.authService.obtenerToken();
    const token = tokenRes.access_token;
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'session_id': this.sessionService.getToken() || '' });
    const url = environment.backendUrl + '/esi-expediente-trazabilidad' + `?exp_id=${expId}`;
    const body = {};
    return firstValueFrom(this.http.post<any>(url, body, { headers })).then(res => res.data as TrazabilidadBitacora[]);
  }

  async obtenerExpedienteDetalle(expId: number): Promise<ExpedienteCompletoResponse> {
    const tokenRes = await this.authService.obtenerToken();
    const token = tokenRes.access_token;
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'session_id': this.sessionService.getToken() || '' });
    const url = environment.backendUrl + '/esi-expediente-detalle' + `?exp_id=${expId}`;
    return firstValueFrom(this.http.post<{ success: string, data: ExpedienteCompletoResponse, msg: string }>(url, {exp_id: expId}, { headers }))
      .then(res => res.data);
  }

  async crearApelacion(payload: { exp_id: number; usu_rut: number | string; ape_motivo: string; documentos: { doc_nombre_archivo: string; archivo: string }[] }): Promise<any> {
    const tokenRes = await this.authService.obtenerToken();
    const token = tokenRes.access_token;
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'session_id': this.sessionService.getToken() || '' });
    const url = environment.backendUrl + '/esi-apelacion';
    return firstValueFrom(this.http.post(url, payload, { headers }));
  }

  async emitirResolucion(payload: { exp_id: number }): Promise<any> {
    const tokenRes = await this.authService.obtenerToken();
    const token = tokenRes.access_token;
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'session_id': this.sessionService.getToken() || '' });
    const url = environment.backendUrl + '/esi-resolucion';
    return firstValueFrom(this.http.post(url, payload, { headers }));
  }
}
